<script setup lang="ts">
import { onUnmounted, ref, useTemplateRef, watch } from "vue"

import { useBarcodeScanner } from "@/composables/useBarcodeScanner"
import { useNativeCamera } from "@/composables/useNativeCamera"
import { errorMessage } from "@/lib/errors"
import { ICON } from "@/lib/icons"

const props = defineProps<{
  open: boolean
  statusOverride?: string | null
}>()

const emit = defineEmits<{
  "update:open": [value: boolean]
  scan: [code: string]
}>()

const videoEl = useTemplateRef<HTMLVideoElement>("videoEl")
const stream = ref<MediaStream | null>(null)
const cameraError = ref<string | null>(null)

const scanner = useBarcodeScanner((code) => emit("scan", code))
const nativeCamera = useNativeCamera()

let startGeneration = 0

async function startCamera() {
  const thisGeneration = ++startGeneration
  try {
    const mediaStream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: "environment" },
    })
    if (thisGeneration !== startGeneration || !videoEl.value) {
      mediaStream.getTracks().forEach((track) => track.stop())
      return
    }
    stream.value = mediaStream
    videoEl.value.srcObject = mediaStream
    await videoEl.value.play()
    scanner.start(videoEl.value)
  } catch (err) {
    if (thisGeneration !== startGeneration) return
    cameraError.value = errorMessage(err, "Camera not available")
  }
}

function stopCamera() {
  startGeneration++
  scanner.stop()
  stream.value?.getTracks().forEach((track) => track.stop())
  stream.value = null
  cameraError.value = null
}

function close() {
  emit("update:open", false)
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === "Escape") close()
}

watch(
  () => props.open,
  (open) => {
    if (open) {
      nativeCamera.setSystemChrome(true)
      startCamera()
      document.addEventListener("keydown", onKeydown)
    } else {
      nativeCamera.setSystemChrome(false)
      stopCamera()
      document.removeEventListener("keydown", onKeydown)
    }
  },
)

onUnmounted(() => {
  nativeCamera.setSystemChrome(false)
  stopCamera()
  document.removeEventListener("keydown", onKeydown)
})
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        role="dialog"
        aria-modal="true"
        aria-label="Scan barcode or QR code"
        class="fixed inset-0 z-50 flex flex-col bg-black"
      >
        <!-- Camera feed -->
        <video
          ref="videoEl"
          autoplay
          playsinline
          muted
          class="absolute inset-0 h-full w-full object-cover"
        />

        <!-- Camera error fallback -->
        <div
          v-if="cameraError"
          class="absolute inset-0 flex items-center justify-center bg-neutral-900"
        >
          <span class="text-sm text-white/50">{{ cameraError }}</span>
        </div>

        <!-- Close button -->
        <div
          data-testid="scanner-header"
          class="relative z-10 flex items-center justify-end px-4"
          :style="{
            height: 'calc(4rem + var(--safe-top))',
            paddingTop: 'var(--safe-top)',
            paddingRight: 'max(1rem, var(--safe-right))',
            paddingLeft: 'max(1rem, var(--safe-left))',
          }"
        >
          <UButton
            :icon="ICON.close"
            aria-label="Close scanner"
            color="neutral"
            variant="ghost"
            size="lg"
            class="text-white"
            square
            @click="close"
          />
        </div>

        <!-- Bracket overlay -->
        <div class="pointer-events-none relative z-10 flex flex-1 items-center justify-center">
          <div class="relative h-48 w-48">
            <div
              class="absolute top-0 left-0 h-8 w-8 rounded-tl-lg border-t-2 border-l-2 border-white/70"
            />
            <div
              class="absolute top-0 right-0 h-8 w-8 rounded-tr-lg border-t-2 border-r-2 border-white/70"
            />
            <div
              class="absolute bottom-0 left-0 h-8 w-8 rounded-bl-lg border-b-2 border-l-2 border-white/70"
            />
            <div
              class="absolute right-0 bottom-0 h-8 w-8 rounded-br-lg border-r-2 border-b-2 border-white/70"
            />
          </div>
        </div>

        <!-- Status pill -->
        <div
          data-testid="scanner-status"
          class="relative z-10 flex justify-center px-4"
          :style="{
            paddingRight: 'max(1rem, var(--safe-right))',
            paddingBottom: 'calc(3rem + var(--safe-bottom))',
            paddingLeft: 'max(1rem, var(--safe-left))',
          }"
        >
          <div
            role="status"
            aria-live="polite"
            class="rounded-full bg-black/60 px-4 py-2 text-sm text-white/80"
          >
            {{ statusOverride ?? "Point at a barcode or QR code" }}
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
